// Redux slice for Task management with server-side pagination
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TaskService } from '@/api/generated/services/TaskService';
import type { TaskFullResponse } from '@/api/generated/models/TaskFullResponse';
import { handleAsyncThunkError } from './errorUtils';

// State interface
interface TaskState {
  // Data related
  items: TaskFullResponse[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;

  // Pagination related
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;

  // Caching related
  loadedPages: Set<number>;

  // Filter parameters
  filters: {
    workspaceId?: string;
    q?: string;
    startDate?: string;
    endDate?: string;
    departmentId?: string;
    businessId?: string;
    assigneeId?: string;
  };
}

// Initial state
const initialState: TaskState = {
  items: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  currentPage: 1,
  pageSize: 100,
  totalItems: 0,
  totalPages: 0,
  hasNext: false,
  hasPrev: false,
  loadedPages: new Set(),
  filters: {},
};

// Async thunks
export const fetchTasks = createAsyncThunk(
  'task/fetchTasks',
  async (params: {
    workspaceId: string;
    q?: string;
    startDate?: string;
    endDate?: string;
    departmentId?: string;
    businessId?: string;
    assigneeId?: string;
    subAssigneeId?: string;
    managerId?: string;
    reviewerId?: string;
    page?: number;
    perPage?: number;
    reset?: boolean;
  }, { rejectWithValue }) => {
    try {
      const response = await TaskService.appsTaskViewsListTasks({
        workspaceId: params.workspaceId,
        q: params.q,
        startDate: params.startDate,
        endDate: params.endDate,
        departmentId: params.departmentId,
        businessId: params.businessId,
        assigneeId: params.assigneeId,
        subAssigneeId: params.subAssigneeId,
        managerId: params.managerId,
        reviewerId: params.reviewerId,
        page: params.page || 1,
        perPage: params.perPage || 100
      });

      return {
        data: (response.data || []) as TaskFullResponse[],
        meta: response.meta || {
          page: 1,
          per_page: 100,
          total: 0,
          pages: 1,
          has_next: false,
          has_prev: false
        },
        reset: params.reset || false,
        page: params.page || 1,
        filters: {
          workspaceId: params.workspaceId,
          q: params.q,
          startDate: params.startDate,
          endDate: params.endDate,
          departmentId: params.departmentId,
          businessId: params.businessId,
          assigneeId: params.assigneeId
        }
      };
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '과제 목록 조회에 실패했습니다.');
    }
  }
);

export const fetchMoreTasks = createAsyncThunk(
  'task/fetchMoreTasks',
  async (params: {
    workspaceId: string;
    q?: string;
    startDate?: string;
    endDate?: string;
    departmentId?: string;
    businessId?: string;
    assigneeId?: string;
    subAssigneeId?: string;
    managerId?: string;
    reviewerId?: string;
    page: number;
    perPage?: number;
  }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { task: TaskState };
      const { loadedPages } = state.task;

      // Check if page already loaded
      if (loadedPages.has(params.page)) {
        return null;
      }

      const response = await TaskService.appsTaskViewsListTasks({
        workspaceId: params.workspaceId,
        q: params.q,
        startDate: params.startDate,
        endDate: params.endDate,
        departmentId: params.departmentId,
        businessId: params.businessId,
        assigneeId: params.assigneeId,
        subAssigneeId: params.subAssigneeId,
        managerId: params.managerId,
        reviewerId: params.reviewerId,
        page: params.page,
        perPage: params.perPage || 100
      });

      return {
        data: (response.data || []) as TaskFullResponse[],
        meta: response.meta || {
          page: params.page,
          per_page: 100,
          total: 0,
          pages: 1,
          has_next: false,
          has_prev: false
        },
        page: params.page
      };
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '추가 과제 목록 조회에 실패했습니다.');
    }
  }
);

// Slice
const taskSlice = createSlice({
  name: 'task',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    resetTaskData: (state) => {
      state.items = [];
      state.loadedPages = new Set();
      state.currentPage = 1;
      state.totalItems = 0;
      state.totalPages = 0;
      state.hasNext = false;
      state.hasPrev = false;
      state.filters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, meta, reset, page, filters } = action.payload;

        if (reset) {
          // Reset data and set new
          state.items = data;
          state.loadedPages = new Set([page]);
          state.filters = filters;
        } else {
          // Add to existing data (remove duplicates)
          const existingIds = new Set(state.items.map((item: TaskFullResponse) => item.id));
          const newItems = data.filter((item: TaskFullResponse) => !existingIds.has(item.id));
          state.items.push(...newItems);
          state.loadedPages.add(page);
        }

        // Update meta information
        state.currentPage = meta.page;
        state.pageSize = meta.per_page;
        state.totalItems = meta.total;
        state.totalPages = meta.pages;
        state.hasNext = meta.has_next;
        state.hasPrev = meta.has_prev;
        state.error = null;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch more tasks
      .addCase(fetchMoreTasks.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(fetchMoreTasks.fulfilled, (state, action) => {
        state.isLoadingMore = false;

        if (action.payload) {
          const { data, meta, page } = action.payload;

          // Add to existing data (remove duplicates)
          const existingIds = new Set(state.items.map((item: TaskFullResponse) => item.id));
          const newItems = data.filter((item: TaskFullResponse) => !existingIds.has(item.id));
          state.items.push(...newItems);
          state.loadedPages.add(page);

          // Update meta information
          state.totalItems = meta.total;
          state.totalPages = meta.pages;
          state.hasNext = meta.has_next;
          state.hasPrev = meta.has_prev;
        }

        state.error = null;
      })
      .addCase(fetchMoreTasks.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setCurrentPage,
  setPageSize,
  resetTaskData
} = taskSlice.actions;

export default taskSlice.reducer;